import PropTypes from "prop-types";
import DateCalendarInput from "app/pages/SmartDP/Search/DateCalendarInput/DateCalendarInput";
import { memo, useCallback, useEffect } from "react";
import { addDays, format } from "date-fns";
import { useField } from "formik";
import { SDP_ROTATION_DATE_FORMAT } from "app/constants";
import { isDateWithinRange } from "app/utils/utils";

const MIN_CALENDAR_DATE_OFFSET = 2;
const MAX_CALENDAR_DATE_OFFSET = 340;
const MIN_BOOKING_DATE_OFFSET = 3;
const MAX_BOOKING_DATE_OFFSET = 350;

const StopoverDateCalendarContainer = props => {
	const {
		updateFloatingPositionReference = () => {},
		handleDayChange = () => {},
		popperOffset = [],
		isHotelOnly = false,
	} = props;
	const [field] = useField(props);
	const [departureCityField] = useField("departureCity");
	const [destinationResortField] = useField("destinationResort");

	const selectedDepartureDate = field.value?.departureDate;
	const selectedEndDate = field.value?.endDate;

	const today = new Date();

	const stopoverCalendarMinDate = format(
		addDays(today, MIN_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);
	const stopoverCalendarMaxDate = format(
		addDays(today, MAX_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);
	const calendarMinDate = stopoverCalendarMinDate;
	let calendarMaxDate = stopoverCalendarMaxDate;

	const maxSelectableDate = addDays(today, MAX_BOOKING_DATE_OFFSET);

	if (selectedDepartureDate && !selectedEndDate) {
		calendarMaxDate = format(maxSelectableDate, SDP_ROTATION_DATE_FORMAT);
	}
	const isCalendarDisabled = isHotelOnly
		? !destinationResortField.value
		: !departureCityField.value && !destinationResortField.value;

	const checkDateAvailability = useCallback(date => {
		if (isCalendarDisabled) {
			return true;
		}
		if (selectedDepartureDate && !selectedEndDate) {
			const minEndDate = addDays(new Date(selectedDepartureDate), MIN_BOOKING_DATE_OFFSET);
			const calendarMinDate = addDays(today, MIN_CALENDAR_DATE_OFFSET);

			// Enable valid end dates : [departure + 3, departure + 350]
			if (isDateWithinRange(date, minEndDate, maxSelectableDate)) {
				return false;
			}
			// Enable anterior re-selection as new departure : [today + 2, departure]
			if (isDateWithinRange(date, calendarMinDate, selectedDepartureDate)) {
				return false;
			}
			return true;
		}

		return false;
	});
	useEffect(() => {
		if (selectedDepartureDate && selectedEndDate) {
			handleDayChange({
				selectedEndDate,
				selectedDepartureDate,
			});
		} else if (!selectedDepartureDate && !selectedEndDate) {
			handleDayChange({
				selectedEndDate,
				selectedDepartureDate,
			});
		}
	}, [selectedDepartureDate, selectedEndDate]);

	return (
		<DateCalendarInput
			{...props}
			departureDateMin={calendarMinDate}
			departureDateMax={calendarMaxDate}
			updateFloatingPositionReference={updateFloatingPositionReference}
			checkDateAvailability={checkDateAvailability}
			handleDayChange={handleDayChange}
			isCalendarDisabled={isCalendarDisabled}
			popperOffset={popperOffset}
			{...isHotelOnly && {
				displayCalendarIcon: false,
				displayFooterLabel: false,
			}}
		/>
	);
};

StopoverDateCalendarContainer.propTypes = {
	updateFloatingPositionReference: PropTypes.func,
	handleDayChange: PropTypes.func,
	isHotelOnly: PropTypes.bool,
	popperOffset: PropTypes.array,
};

export default memo(StopoverDateCalendarContainer);
